// WebSocket manager for multiplayer Crowd game
class CrowdGameSocket {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
  }

  connect(userId, gameId) {
    try {
      // Use your WebSocket server URL - you'll need to set up a WebSocket server
      const wsUrl = 'wss://beatthebook.app'; //ws://localhost:8080
      this.socket = new WebSocket(`${wsUrl}/crowd-game`);
      
      this.socket.onopen = () => {
        console.log('Connected to Crowd game WebSocket');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Send join message
        this.send('join', {
          userId,
          gameId,
          timestamp: Date.now()
        });
        
        this.emit('connected');
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.emit(data.type, data.payload);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = () => {
        console.log('Disconnected from Crowd game WebSocket');
        this.isConnected = false;
        this.emit('disconnected');
        this.attemptReconnect(userId, gameId);
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };

    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

  send(type, payload) {
    console.log('🚀 Sending WebSocket message:', type, payload);
    
    if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ type, payload });
      console.log('✅ Message sent successfully:', message);
      this.socket.send(message);
    } else {
      console.warn('❌ WebSocket not connected, cannot send message:', type, {
        isConnected: this.isConnected,
        socketExists: !!this.socket,
        readyState: this.socket?.readyState
      });
    }
  }

  attemptReconnect(userId, gameId) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(userId, gameId);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
      this.emit('max_reconnect_attempts_reached');
    }
  }

  // Event listener system
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event callback:', error);
        }
      });
    }
  }

  // Game-specific methods
  updatePlayerPosition(position) {
    this.send('player_position', {
      position,
      timestamp: Date.now()
    });
  }

  updateBallPosition(position, velocity) {
    this.send('ball_update', {
      position,
      velocity,
      timestamp: Date.now()
    });
  }

  sendGameResult(result) {
    this.send('game_result', {
      result,
      timestamp: Date.now()
    });
  }

  startGame() {
    this.send('start_game', {
      timestamp: Date.now()
    });
  }

  endGame(result) {
    this.send('end_game', {
      result,
      timestamp: Date.now()
    });
  }

  // Question synchronization methods
  broadcastNewQuestion(question) {
    this.send('new_question', {
      question,
      timestamp: Date.now()
    });
  }

  broadcastQuestionUpdate(questionId, update) {
    this.send('question_update', {
      questionId,
      update,
      timestamp: Date.now()
    });
  }
}

// Singleton instance
let crowdGameSocket = null;

export const getCrowdGameSocket = () => {
  if (!crowdGameSocket) {
    crowdGameSocket = new CrowdGameSocket();
  }
  return crowdGameSocket;
};

export default getCrowdGameSocket;
